import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../CoinsAllotments/CoinAllotmentOverlay.css";
import axios from "axios";
import { Api } from "../../BackendApi/api";
import ConfirmPopup from "../../Component/ConfirmPopup/ConfirmPopup";
import CustomerOverlay from "../Customer/CustomerOverlay";

let typingTimer;

const CoinAllotmentOverlay = ({ onClose, onSave, customerData }) => {
    const [formData, setFormData] = useState({
        customer: "",
        customerId: "",
        dateTime: new Date(),
        coins: "",
        remarks: "",
        BillInfo: "",
    });

    const [loading, setLoading] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [popup, setPopup] = useState({
        show: false,
        message: "",
        type: "success",
    });

    const [showCustomerOverlay, setShowCustomerOverlay] = useState(false);
    const coinallotRef = useRef(null);

    /* ===============================
     ✅ AUTO FILL CUSTOMER NAME
     =============================== */
    useEffect(() => {
        if (customerData?.name) {
            setFormData((prev) => ({
                ...prev,
                customer: customerData.name,
                customerId: customerData.id,
            }));
            setCustomers([]);
            setShowDropdown(false);
        }

        if (coinallotRef.current) {
            coinallotRef.current.focus();
        }
    }, [customerData]);

    /* ===============================
     FETCH CUSTOMERS (unchanged UI)
     =============================== */
    const fetchCustomers = async (search) => {
        if (!search.trim() || customerData) return;

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.post(
                `${Api.baseurl}/getcustomers`,
                {
                    searchstring: search,
                    startfrom: 0,
                    limit: 50,
                    SortColumn: "CreatedAt",
                    SortOrder: -1,
                    Status: "ACTIVE",
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            const customerArray =
                response.data.data || response.data.customers || [];

            const list = customerArray.map((item) => ({
                id: item._id,
                name: item.CustName || "N/A",
                mobile: item.CustMobile || "N/A",
                coins:
                    item.CustCurrentCoins != null ? item.CustCurrentCoins : 0,
            }));

            setCustomers(list);
            setShowDropdown(true);
        } catch (err) {
            console.error("Error fetching customers:", err);
        } finally {
            setLoading(false);
        }
    };

    /* ===============================
     INPUT HANDLER (LOCK WHEN AUTO)
     =============================== */
    const handleCustomerInput = (e) => {
        if (customerData) return; // no typing if auto-filled

        const value = e.target.value;
        setFormData({ ...formData, customer: value });

        clearTimeout(typingTimer);
        typingTimer = setTimeout(() => {
            fetchCustomers(value);
        }, 500);
    };

    const handleSelectCustomer = (cust) => {
        setFormData({
            ...formData,
            customer: cust.name,
            customerId: cust.id,
        });
        setCustomers([]);
        setShowDropdown(false);
    };

    const handleDateChange = (date) => {
        setFormData({ ...formData, dateTime: date });
    };

    const submitTransaction = async (type) => {
        if (loading) return;
        if (!formData.customer || !formData.coins) {
            setPopup({
                show: true,
                message: "Please select a customer and enter coins.",
                type: "warning",
            });
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const transDateISO = formData.dateTime
                ? new Date(formData.dateTime).toISOString()
                : new Date().toISOString();
            const entryDateISO = new Date().toISOString();
            const payload = {
                CustName: formData.customer,
                TransValue: Number(formData.coins),

                TransType: type,
                TransDesc: formData.remarks || "Coins Transaction",
                BillInfo: formData.BillInfo || "",
                TransDate: transDateISO,
                CreatedAt: entryDateISO,
            };

            const response = await axios.post(Api.coinAllotment, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (response.data.success || response.data.status === 1) {
                setPopup({
                    show: true,
                    message: `Coins ${
                        type === "ALLOT" ? "Allotted" : "Deducted"
                    } Successfully`,
                    type: "success",
                });
            } else {
                setPopup({
                    show: true,
                    message: response.data.message || "Transaction failed",
                    type: "error",
                });
            }
        } catch (err) {
            setPopup({
                show: true,
                message: "Something went wrong",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePopupClose = () => {
        setPopup({ ...popup, show: false });
        if (popup.type === "success") {
            onSave?.();
            onClose();
        }
    };

    return (
        <div className="coinallot-overlay">
            <div className="coinallot-overlay-content">
                <h2>Coin Allotment</h2>

                <form className="coinallot-overlay-form">
                    {/* 🔴 DESIGN SAME – INPUT SAME */}
                    <label>
                        Customer
                        <div
                            className="customer-field"
                            style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <input
                                type="text"
                                name="customer"
                                value={formData.customer}
                                onChange={handleCustomerInput}
                                ref={coinallotRef}
                                readOnly={!!customerData}
                                onFocus={() =>
                                    !customerData &&
                                    formData.customer &&
                                    setShowDropdown(true)
                                }
                            />

                            {!customerData && (
                                <button
                                    type="button"
                                    className="add-customer-btn"
                                    onClick={() => setShowCustomerOverlay(true)}
                                >
                                    +
                                </button>
                            )}

                            {!customerData &&
                                showDropdown &&
                                customers.length > 0 && (
                                    <div className="customer-dropdown">
                                        {customers.map((cust) => (
                                            <div
                                                key={cust.id}
                                                className="customer-option"
                                                onClick={() =>
                                                    handleSelectCustomer(cust)
                                                }
                                            >
                                                {cust.name} - Coins:{" "}
                                                {cust.coins}
                                            </div>
                                        ))}
                                    </div>
                                )}
                        </div>
                    </label>

                    {/* 🔴 REST ALL SAME */}
                    <label>
                        Date & Time
                        <DatePicker
                            selected={formData.dateTime}
                            onChange={handleDateChange}
                            showTimeSelect
                            dateFormat="dd/MM/yyyy h:mm aa"
                            className="datepicker-input"
                        />
                    </label>

                    <label>
                        Bill Info
                        <input
                            type="text"
                            name="billInfo"
                            value={formData.BillInfo}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    BillInfo: e.target.value,
                                })
                            }
                        />
                    </label>

                    <label>
                        Remarks
                        <input
                            type="text"
                            value={formData.remarks}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    remarks: e.target.value,
                                })
                            }
                        />
                    </label>

                    <label>
                        Coins
                        <div className="coins-input-wrapper">
                            <input
                                type="text"
                                value={formData.coins}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        coins: e.target.value,
                                    })
                                }
                            />
                            <span className="coins-label">Coins</span>
                        </div>
                    </label>

                    <div className="coinallot-overlay-buttons full-width">
                        <button
                            disabled={loading}
                            type="submit"
                            className="coin-btn-allot"
                            onClick={(e) => {
                                e.preventDefault();
                                submitTransaction("ALLOT");
                            }}
                        >
                            <span className="symbol">+</span> Allot
                        </button>

                        <button
                            disabled={loading}
                            type="submit"
                            className="coin-btn-deduct"
                            onClick={(e) => {
                                e.preventDefault();
                                submitTransaction("DEDUCT");
                            }}
                        >
                            <span className="symbol">−</span> Deduct
                        </button>

                        <button type="button" onClick={onClose}>
                            Cancel
                        </button>
                    </div>
                </form>
            </div>

            <ConfirmPopup
                show={popup.show}
                message={popup.message}
                type={popup.type}
                onClose={handlePopupClose}
            />

            {!customerData && showCustomerOverlay && (
                <CustomerOverlay
                    onClose={() => setShowCustomerOverlay(false)}
                    onSave={() => setShowCustomerOverlay(false)}
                />
            )}
        </div>
    );
};

export default CoinAllotmentOverlay;
